/**
 * ColumnGraphRow — `column.graph` renderer. Paints a per-cell sparkline
 * from a `(number, per-cell)` source; each cell holds a `number[]`.
 */

import type { Option } from "../../core/option.ts";
import { Json } from "../../core/json.ts";
import type { NaiveDate } from "@tento-chrono";

import { RendererRow } from "./renderer-row.ts";
import { SourceCells } from "./source-cells.ts";
import { SourceRow } from "./source-row.ts";
import type { Actor } from "../../tx/invalidation.ts";
import { sourceWriterOf } from "./source-row.ts";

/**
 * **The writer of a `column.graph` renderer** — the checked series write of
 * its source row's cells (`ColumnGraphRow.Writer`), through the source's
 * checked writer (`sourceWriterOf`). Not on the row: a
 * row a read hands out (`handle.model`, `row.variant`, `RowKind.of`) —
 * and the live class `liveRowOf` reaches — carries no writer. The package's
 * own ops write through it, and this repository's tests through
 * `@tento-lona/sheets/internal-testing`; an app and the SDK's own front-ends
 * (sheets-ui, the CLI) write the cell through the SDK row's checked verbs
 * (`row.writableSingle()?.write(CellTarget.date(day), value, actor)`).
 */
export function columnGraphWriterOf(row: ColumnGraphRow): ColumnGraphRow.Writer {
  return Object.freeze({
    row,
    write: async (date: NaiveDate.Partial, values: readonly number[], actor: Actor) => {
      await sourceWriterOf(row.source).write<readonly number[]>(date, values, null, actor);
    },
  });
}

/**
 * The `column.graph` renderer row: paints a sparkline per cell from its source row's number lists.
 */
export class ColumnGraphRow extends RendererRow {
  get source(): SourceRow {
    const p = this.parent;
    if (!(p instanceof SourceRow)) {
      throw new Error(
        `ColumnGraphRow: parent of ${this.id.rawId} is not a SourceRow ` +
          `(got ${p?.constructor.name ?? "undefined"}).`,
      );
    }
    return p;
  }

  peekSeries(date: NaiveDate.Partial): Option<readonly number[]> {
    const cell = SourceCells.dates(this.source.peekCells(date.toRange()))[0];
    return cell == null ? null : (cell.value as Option<readonly number[]>);
  }


  /**
   * Y-axis multiplier applied to each cell value before plotting.
   * Stored on the source's attribute bag; the `graph-plugin`
   * consumes this via `row.scale` on the renderer subclass.
   */
  get scale(): number {
    return Json.number(this.source.attributes(), "scale") ?? 30;
  }
}

/** Types that go with `ColumnGraphRow`: `Writer`. */
export namespace ColumnGraphRow {
  /** A `column.graph` renderer's writer, reached through `columnGraphWriterOf(row)` only. */
  export interface Writer {
    /** The row this writer writes. */
    readonly row: ColumnGraphRow;
    /** Checked write of the number list for `date`. */
    write(date: NaiveDate.Partial, values: readonly number[], actor: Actor): Promise<void>;
  }
}
